import { v4 as uuidv4 } from "uuid";
import IItemWithSignatureRaw from "../IItemWithSignatureRaw";
import ITrackableStock from "../ITrackableStock";
import dayjs from "dayjs";
import Item from "./Item";
import IForeignItem from "../responses/IForeignItem";

export enum ItemSigInvalidFields {
  None = 0,
  Quantity = 1 << 0, // 1
  Lot = 1 << 1, // 2
  BestBefore = 1 << 2, // 4
  Expiry = 1 << 3, // 8
  All = Quantity | Lot | BestBefore | Expiry, // 15
}

class SignatureItem implements ITrackableStock, IItemWithSignatureRaw {
  public id: string = uuidv4();
  public availableQuantity: number = 0;

  constructor(
    public item: Item,
    public unitCode: string,
    public quantity: number,
    lot: string | undefined = undefined,
    public bestBefore: string | undefined = undefined,
    public expiry: string | undefined = undefined,
    public isInbound: boolean = false,
    public trackStock: boolean = true,
    public foreignItem: IForeignItem | undefined = undefined,
  ) {
    this.lot = lot;
  }

  _lot = undefined as string | undefined;

  get lot(): string | undefined {
    return this._lot;
  }
  set lot(value: string | undefined) {
    this._lot = value?.trim().toLocaleUpperCase();
  }

  get bestBeforeAsDate(): Date | undefined {
    return this.optionalDate(this.bestBefore);
  }

  get expiryAsDate(): Date | undefined {
    return this.optionalDate(this.expiry);
  }

  get bestBeforeExceeded(): boolean {
    return this.bestBeforeAsDate
      ? dayjs(this.bestBeforeAsDate).isBefore(dayjs())
      : false;
  }

  get expiryExceeded(): boolean {
    return this.expiryAsDate
      ? dayjs(this.expiryAsDate).isBefore(dayjs())
      : false;
  }

  get bestBeforeLeadTimeReached(): boolean {
    return this.bestBeforeAsDate
      ? dayjs(this.bestBeforeAsDate)
          .subtract(this.item.bestBeforeLeadTime, "day")
          .isBefore(dayjs())
      : false;
  }

  get expiryLeadTimeReached(): boolean {
    return this.expiryAsDate
      ? dayjs(this.expiryAsDate)
          .subtract(this.item.expirationLeadTime, "day")
          .isBefore(dayjs())
      : false;
  }

  get isQuantityValid(): boolean {
    if (!this.quantity) {
      return false;
    }
    return this.quantity > 0;
  }

  get isLotValid(): boolean {
    if (!this.item) {
      return false;
    }
    if (!this.item.trackLotNumber || this.item.allowCalculatedLot) {
      return true;
    }
    return !!this.lot;
  }

  get isBestBeforeValid(): boolean {
    if (!this.item) {
      return false;
    }
    if (!this.item.trackBestBeforeDate || this.item.allowCalculatedLot) {
      return true;
    }
    if (!this.bestBefore) {
      return false;
    }
    return this.isInbound ? new Date(this.bestBefore) >= new Date() : true; // not checking date; just check if it is set
  }

  get isExpiryValid(): boolean {
    if (!this.item) {
      return false;
    }
    if (!this.item.trackExpirationDate || this.item.allowCalculatedLot) {
      return true;
    }
    if (!this.expiry) {
      return false;
    }
    return this.isInbound ? new Date(this.expiry) >= new Date() : true; // not checking date; just check if it is set
  }

  get isValid(): boolean {
    if (!this.isQuantityValid) {
      return false;
    }
    if (!this.isLotValid) {
      return false;
    }
    if (!this.isBestBeforeValid) {
      return false;
    }
    if (!this.isExpiryValid) {
      return false;
    }
    return true;
  }

  get isValidSignature(): boolean {
    if (!this.isLotValid) {
      return false;
    }
    if (!this.isBestBeforeValid) {
      return false;
    }
    if (!this.isExpiryValid) {
      return false;
    }
    return true;
  }

  get invalidFields(): ItemSigInvalidFields {
    let invalidField: ItemSigInvalidFields = ItemSigInvalidFields.None;
    if (!this.isQuantityValid) {
      invalidField |= ItemSigInvalidFields.Quantity;
    }
    if (!this.isLotValid) {
      invalidField |= ItemSigInvalidFields.Quantity;
    }
    if (!this.isBestBeforeValid) {
      invalidField |= ItemSigInvalidFields.Quantity;
    }
    if (!this.isExpiryValid) {
      invalidField |= ItemSigInvalidFields.Quantity;
    }
    return invalidField;
  }

  optionalDate(value: string | undefined): Date | undefined {
    return value ? new Date(value) : undefined;
  }

  equals(other: SignatureItem): boolean {
    return (
      this.item.id === other.item.id &&
      this.lot === other.lot &&
      this.bestBefore === other.bestBefore &&
      this.expiry === other.expiry
    );
  }
}

export default SignatureItem;
